/**
 * Checklist de testes por categoria de equipamento.
 *
 * - O MODELO (template) fica na categoria: seções → itens.
 * - Cada OS guarda uma CÓPIA do modelo no momento em que o checklist é iniciado, mais as respostas.
 *   Assim, editar o modelo depois não bagunça checklists já preenchidos.
 */

/**
 * Tipos de item:
 * - test:  Resultado OK / Falha / N/A, com método/ferramenta sugerido e observação (tabelas do checklist).
 * - check: Caixa de marcação simples (ex.: "Acessórios conferidos").
 * - yesno: Pergunta Sim / Não (ex.: "Existem sinais de queda ou líquido?").
 * - text:  Texto livre (ex.: "Diagnóstico / causa provável").
 */
export const ENTRY_CHECKLIST_VALUES = ['OK', 'DEFECT', 'NOT_TESTED'] as const;
export type EntryChecklistValue = (typeof ENTRY_CHECKLIST_VALUES)[number];
export type EntryChecklistAnswers = Record<string, { value?: EntryChecklistValue | null; text?: string | null }>;
export const ENTRY_CHECKLIST_VALUE_LABELS: Record<EntryChecklistValue, string> = { OK: 'Ok', DEFECT: 'Defeito', NOT_TESTED: 'Não testado' };
export function entryChecklistProgress(template: ChecklistTemplate, answers: EntryChecklistAnswers) {
  const items = template.flatMap(s => s.items);
  return { total: items.length, answered: items.filter(i => !!answers[i.id]?.value).length, failures: items.filter(i => answers[i.id]?.value === 'DEFECT').length };
}

export const CHECKLIST_ITEM_KINDS = ['test', 'check', 'yesno', 'text'] as const;
export type ChecklistItemKind = (typeof CHECKLIST_ITEM_KINDS)[number];

export const CHECKLIST_ITEM_KIND_LABELS: Record<ChecklistItemKind, string> = {
  test: 'Teste (OK / Falha / N/A)',
  check: 'Marcação',
  yesno: 'Sim / Não',
  text: 'Texto livre',
};

export const CHECKLIST_VALUES = ['OK', 'FAIL', 'NA', 'YES', 'NO', 'CHECKED'] as const;
export type ChecklistValue = (typeof CHECKLIST_VALUES)[number];

/** Valores aceitos por tipo de item. */
export const CHECKLIST_VALUES_BY_KIND: Record<ChecklistItemKind, readonly ChecklistValue[]> = {
  test: ['OK', 'FAIL', 'NA'],
  check: ['CHECKED'],
  yesno: ['YES', 'NO'],
  text: [],
};

export const CHECKLIST_VALUE_LABELS: Record<ChecklistValue, string> = {
  OK: 'OK',
  FAIL: 'Falha',
  NA: 'N/A',
  YES: 'Sim',
  NO: 'Não',
  CHECKED: 'Feito',
};

export interface ChecklistItem {
  id: string;
  label: string;
  kind: ChecklistItemKind;
  /** Método / ferramenta sugerida (ex.: "MemTest86", "CrystalDiskInfo"). */
  hint?: string | null;
}

export interface ChecklistSection {
  id: string;
  title: string;
  /** Observação exibida abaixo da seção (ex.: critério recomendado). */
  note?: string | null;
  items: ChecklistItem[];
}

export type ChecklistTemplate = ChecklistSection[];

export interface ChecklistAnswer {
  value?: ChecklistValue | null;
  /** Observação do item (ou o conteúdo, para itens de texto livre). */
  text?: string | null;
}

export type ChecklistAnswers = Record<string, ChecklistAnswer>;

/** Um item conta como respondido quando tem valor (ou texto, se for de texto livre). */
export function isAnswered(item: Pick<ChecklistItem, 'kind'>, answer: ChecklistAnswer | undefined): boolean {
  if (!answer) return false;
  if (item.kind === 'text') return !!answer.text?.trim();
  return !!answer.value;
}

export function checklistProgress(template: ChecklistTemplate, answers: ChecklistAnswers) {
  let total = 0;
  let answered = 0;
  let failures = 0;
  for (const section of template) {
    for (const item of section.items) {
      total++;
      const a = answers[item.id];
      if (isAnswered(item, a)) answered++;
      if (a?.value === 'FAIL') failures++;
    }
  }
  return { total, answered, failures };
}

/** Mantém só respostas de itens que existem no modelo e com valor compatível com o tipo do item. */
export function sanitizeAnswers(template: ChecklistTemplate, answers: ChecklistAnswers): ChecklistAnswers {
  const out: ChecklistAnswers = {};
  for (const section of template) {
    for (const item of section.items) {
      const a = answers[item.id];
      if (!a) continue;
      const value = a.value && CHECKLIST_VALUES_BY_KIND[item.kind].includes(a.value) ? a.value : null;
      const text = a.text?.trim() ? a.text.trim().slice(0, 2000) : null;
      if (value || text) out[item.id] = { value, text };
    }
  }
  return out;
}

/** Gera um identificador curto para seções e itens novos do modelo. */
export function newChecklistId(prefix = 'i'): string {
  const rnd = Math.random().toString(36).slice(2, 8);
  const time = Date.now().toString(36).slice(-4);
  return `${prefix}${time}${rnd}`;
}
